import { zodResolver } from "@hookform/resolvers/zod";
import { KdsModal } from "@kazaar-platform/design-system";
import { useTranslate } from "@tolgee/react";
import { defaultTo } from "lodash";
import { useState } from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";
import {
  FileContext,
  FileContextProvider,
} from "@/components/FileUploadAsync/FileUploadAsync.context";
import { useOrders, useToasts } from "@/hooks";
import {
  defaultValues,
  type InferredPurchaseOrderFormSchema,
  purchaseOrderFormSchema,
} from "./ModalPurchaseOrderForm.schema";

import { type ModalPurchaseOrderFormProps } from "./ModalPurchaseOrderForm.types";
import { ModalPurchaseOrderFormContent } from "./ModalPurchaseOrderFormContent";

export const ModalPurchaseOrderForm = ({
  isOpen,
  setOpenState,
  orderId,
  orderNumber,
}: ModalPurchaseOrderFormProps) => {
  const { t } = useTranslate();
  const { add } = useToasts();

  // States
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Form
  const methods = useForm<InferredPurchaseOrderFormSchema>({
    defaultValues: defaultValues(defaultTo(orderNumber, "")),
    resolver: zodResolver(purchaseOrderFormSchema),
  });

  // Hooks
  const { useAddPurchaseOrder } = useOrders();
  const { mutateAsync: addPurchaseOrder, isPending } = useAddPurchaseOrder();

  const onError = () => {
    add({
      state: "error",
      title: t("cardOrder.modalAddPurchaseOrder.errorForm"),
    });
  };

  const onSubmit: SubmitHandler<InferredPurchaseOrderFormSchema> = async (
    data
  ) => {
    try {
      await addPurchaseOrder({
        orderId: orderId,
        orderAddPurchaseOrderCommand: data,
      });

      add({
        state: "success",
        title: t("cardOrder.toast.successPurchaseOrder"),
      });
    } catch {
      add({
        state: "error",
        title: t("cardOrder.toast.errorPurchaseOrder"),
      });
    }

    setOpenState(false);
  };

  return (
    <FormProvider {...methods}>
      <FileContextProvider>
        <FileContext.Consumer>
          {(ctx) => (
            <KdsModal
              isOpen={isOpen}
              onOpenChange={() => {
                if (isOpen) {
                  methods.clearErrors();
                }
                setOpenState(!isOpen);
              }}
              dialogProps={{
                dialogHeaderProps: {
                  title: t("cardOrder.modalAddPurchaseOrder.title"),
                },
                content: (
                  <ModalPurchaseOrderFormContent
                    methods={methods}
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                  />
                ),
                dialogFooterProps: {
                  type: "double",
                  buttonProps: [
                    {
                      children: t("buttons.cancel"),
                      variant: "ghost",
                      color: "neutral",
                      disabled: isPending || isLoading || ctx.isUploading,
                      isLoading: isPending || isLoading || ctx.isUploading,
                      onPress: () => {
                        methods.clearErrors();
                        setOpenState(false);
                      },
                    },
                    {
                      children: t("buttons.confirm"),
                      isLoading: isPending || isLoading,
                      disabled: isPending || isLoading || ctx.isUploading,
                      onPress: () =>
                        void methods.handleSubmit(onSubmit, onError)(),
                    },
                  ],
                },
              }}
            />
          )}
        </FileContext.Consumer>
      </FileContextProvider>
    </FormProvider>
  );
};
